import React, { lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { perfMark } from "../utils/perf-tracker";
import { useAuthHook } from "../hooks";

// Helper for named exports (React.lazy requires default export)
const lazyNamed = <T extends Record<string, React.ComponentType>>(
  factory: () => Promise<T>,
  name: keyof T
) => lazy(() => factory().then((m) => ({ default: m[name] })));

// --- Home ---
const HomeDashPage = lazyNamed(
  () => import("../pages/dashboard/home/HomeDashPage"),
  "HomeDashPage"
);

// --- Users ---
const NewUser = lazy(() => import("../pages/dashboard/users/newUser/NewUser"));
const User = lazy(() => import("../pages/dashboard/user/User"));
const Users = lazy(() => import("../pages/dashboard/users/newUser/Users"));

// --- Cleanups ---
const CleanUpsLayoutPage = lazyNamed(
  () => import("../pages/dashboard/cleanUps/CleanUpsLayout"),
  "CleanUpsLayoutPage"
);

// --- RFC ---
const CreateNewRFC = lazyNamed(
  () => import("../pages/dashboard/rfc/CreateNewRFC"),
  "CreateNewRFC"
);
const EditRFC = lazyNamed(
  () => import("../pages/dashboard/rfc/EditRFC"),
  "EditRFC"
);

// --- AuthLogs ---
const AuthLogPage = lazyNamed(
  () => import("../pages/dashboard/authLogs/AuthLogPage"),
  "AuthLogPage"
);

// --- Misc ---
const Misc = lazyNamed(
  () => import("../pages/dashboard/misc/Misc"),
  "Misc"
);

// --- Documentation ---
const DocumentationHub = lazyNamed(
  () => import("../pages/dashboard/documentation/DocumentationHub"),
  "DocumentationHub"
);
const DocumentViewerPage = lazyNamed(
  () => import("../pages/dashboard/documentation/docs/DocumentViewerPage"),
  "DocumentViewerPage"
);
const DocumentEditorPage = lazyNamed(
  () => import("../pages/dashboard/documentation/docs/DocumentEditorPage"),
  "DocumentEditorPage"
);

// --- FCM ---
const FcmIndex = lazyNamed(
  () => import("../pages/dashboard/fcm/FcmIndex"),
  "FcmIndex"
);
const FcmPartnersIndex = lazyNamed(
  () => import("../pages/dashboard/fcm/FcmPartnersIndex"),
  "FcmPartnersIndex"
);
const FcmDogsIndex = lazyNamed(
  () => import("../pages/dashboard/fcm/FcmDogsIndex"),
  "FcmDogsIndex"
);
const FcmTransfersIndex = lazyNamed(
  () => import("../pages/dashboard/fcm/FcmTransfersIndex.jsx"),
  "FcmTransfersIndex"
);
const FcmTransfer = lazyNamed(
  () => import("../pages/dashboard/fcm/FcmTransfer"),
  "FcmTransfer"
);
const FcmPackagesIndex = lazyNamed(
  () => import("../pages/dashboard/fcm/FcmPackagesIndex"),
  "FcmPackagesIndex"
);
const FcmPackage = lazyNamed(
  () => import("../pages/dashboard/fcm/FcmPackage"),
  "FcmPackage"
);

// --- Time Off ---
const TimeOffRequestsLayout = lazyNamed(
  () => import("../pages/dashboard/time-off-requests/TimeOffRequestsLayout"),
  "TimeOffRequestsLayout"
);

// --- Calendar ---
const CalendarPage = lazyNamed(
  () => import("../pages/dashboard/calendar/CalendarPage"),
  "CalendarPage"
);

// --- Tasks ---
const TasksLayout = lazyNamed(
  () => import("../pages/dashboard/tasks/TasksLayout"),
  "TasksLayout"
);
const Kanban = lazyNamed(
  () => import("../pages/dashboard/tasks/components/Kanban"),
  "Kanban"
);
const TaskView = lazyNamed(
  () => import("../pages/dashboard/tasks/components/TaskView"),
  "TaskView"
);
const CreateTask = lazyNamed(
  () => import("../pages/dashboard/tasks/components/CreateTask"),
  "CreateTask"
);
const TaskList = lazyNamed(
  () => import("../pages/dashboard/tasks/components/TaskList"),
  "TaskList"
);
const MyTasks = lazyNamed(
  () => import("../pages/dashboard/tasks/components/MyTasks"),
  "MyTasks"
);
const TaskStats = lazyNamed(
  () => import("../pages/dashboard/tasks/components/TaskStats"),
  "TaskStats"
);
const TaskReport = lazyNamed(
  () => import("../pages/dashboard/tasks/components/TaskReport"),
  "TaskReport"
);

// --- Work Logs ---
const WorkLogsLayout = lazyNamed(
  () => import("../pages/dashboard/workLogs/WorkLogsLayout"),
  "WorkLogsLayout"
);
const AllLogs = lazyNamed(
  () => import("../pages/dashboard/workLogs/components/AllLogs"),
  "AllLogs"
);
const LastLogView = lazyNamed(
  () => import("../pages/dashboard/workLogs/components/LastLogView"),
  "LastLogView"
);
const MyLogs = lazyNamed(
  () => import("../pages/dashboard/workLogs/components/MyLogs"),
  "MyLogs"
);
const RecentLogs = lazyNamed(
  () => import("../pages/dashboard/workLogs/components/RecentLogs"),
  "RecentLogs"
);

// --- Attendance ---
const AttendanceRecordsLayout = lazyNamed(
  () => import("../pages/dashboard/attendanceRecords/AttendanceRecordsLayout"),
  "AttendanceRecordsLayout"
);

// --- Billing (new client invoicing) ---
const NewBillingLayout = lazyNamed(
  () => import("../pages/dashboard/billing/NewBillingLayout"),
  "NewBillingLayout"
);
const CreditNotesPage = lazyNamed(
  () => import("../pages/dashboard/billing/credit-notes/CreditNotesPage"),
  "CreditNotesPage"
);
const GlobalInvoicesPage = lazyNamed(
  () => import("../pages/dashboard/billing/global-invoices/GlobalInvoicesPage"),
  "GlobalInvoicesPage"
);
const ClientListPage = lazyNamed(
  () => import("../pages/dashboard/billing/components/ClientListPage"),
  "ClientListPage"
);
const ClientFormPage = lazyNamed(
  () => import("../pages/dashboard/billing/components/ClientFormPage"),
  "ClientFormPage"
);
const StaffClientInvoicesPage = lazyNamed(
  () => import("../pages/dashboard/billing/components/StaffClientInvoicesPage"),
  "StaffClientInvoicesPage"
);
const StaffInvoiceListPage = lazyNamed(
  () => import("../pages/dashboard/billing/components/StaffInvoiceListPage"),
  "StaffInvoiceListPage"
);
const InvoiceRequestPage = lazyNamed(
  () => import("../pages/dashboard/billing/components/InvoiceRequestPage"),
  "InvoiceRequestPage"
);
const StaffGuidePage = lazyNamed(
  () => import("../pages/dashboard/billing/components/StaffGuidePage"),
  "StaffGuidePage"
);
const StaffInvoiceDetailPage = lazyNamed(
  () => import("../pages/dashboard/billing/components/StaffInvoiceDetailPage"),
  "StaffInvoiceDetailPage"
);

// --- Notifications ---
const Notifications = lazyNamed(
  () => import("../pages/dashboard/notifications/Notifications"),
  "Notifications"
);

// --- HR / Payroll ---
const CollaboratorsLayout = lazyNamed(
  () => import("../pages/dashboard/collaborators/CollaboratorsLayout"),
  "CollaboratorsLayout"
);
const EmploymentsLayout = lazyNamed(
  () => import("../pages/dashboard/collaborators/employments/EmploymentsLayout"),
  "EmploymentsLayout"
);
const PayrollsLayout = lazyNamed(
  () => import("../pages/dashboard/payroll/PayrollsLayout"),
  "PayrollsLayout"
);
const CommissionsLayout = lazyNamed(
  () => import("../pages/dashboard/commissions/CommissionsLayout"),
  "CommissionsLayout"
);
const ActivityRegisterLayout = lazyNamed(
  () => import("../pages/dashboard/activityRegister/ActivityRegisterLayout"),
  "ActivityRegisterLayout"
);

const SimplifiedBranchCashReconciliationLayout = lazyNamed(
  () =>
    import(
      "../pages/dashboard/simplified-branch-cash-reconciliation/SimplifiedBranchCashReconciliationLayout"
    ),
  "SimplifiedBranchCashReconciliationLayout"
);

// --- Controlled Prescriptions ---
const ControlledPrescriptionLayout = lazyNamed(
  () => import("../pages/dashboard/controlled-prescriptions/ControlledPrescriptionLayout"),
  "ControlledPrescriptionLayout"
);

// --- Work Shifts ---
const WeekShiftsLayout = lazyNamed(
  () => import("../pages/dashboard/work-shifts/WeeksShiftsLayout"),
  "WeekShiftsLayout"
);

// --- Former Parameters (now distributed) ---
const JobsLayout = lazyNamed(
  () => import("../pages/dashboard/pararameters/jobs"),
  "JobsLayout"
);
const BranchesLayout = lazyNamed(
  () => import("../pages/dashboard/pararameters/branches/BranchesLayout"),
  "BranchesLayout"
);
const SalaryDataLayout = lazyNamed(
  () => import("../pages/dashboard/pararameters/salary-data/SalaryDataLayout"),
  "SalaryDataLayout"
);
const PublicHolidaysLayout = lazyNamed(
  () => import("../pages/dashboard/pararameters/public-holidays/PublicHolidaysLayout"),
  "PublicHolidaysLayout"
);

// --- Settings ---
const CompanySettingsPage = lazyNamed(
  () => import("../pages/dashboard/settings/CompanySettingsPage"),
  "CompanySettingsPage"
);

// --- QVET ---
const QvetInventoryV2Page = lazyNamed(
  () => import("../pages/dashboard/qvet/inventory"),
  "QvetInventoryV2Page"
);
const QvetSyncLayout = lazyNamed(
  () => import("../pages/dashboard/qvet/sync"),
  "QvetSyncLayout"
);
const QvetInventorySnapshotsPage = lazyNamed(
  () => import("../pages/dashboard/qvet/inventory-snapshots"),
  "QvetInventorySnapshotsPage"
);
const QvetJobsPage = lazyNamed(
  () => import("../pages/dashboard/qvet/jobs"),
  "QvetJobsPage"
);
const QvetMovementsPage = lazyNamed(
  () => import("../pages/dashboard/qvet/movements"),
  "QvetMovementsPage"
);
const QvetPurchasesPage = lazyNamed(
  () => import("../pages/dashboard/qvet/purchases"),
  "QvetPurchasesPage"
);
const QvetSalesPage = lazyNamed(
  () => import("../pages/dashboard/qvet/sales"),
  "QvetSalesPage"
);
const QvetClientsPage = lazyNamed(
  () => import("../pages/dashboard/qvet/clients"),
  "QvetClientsPage"
);
const GoogleContactsPage = lazyNamed(
  () => import("../pages/dashboard/contacts/google-contacts"),
  "GoogleContactsPage"
);
const GoogleCalendarLayout = lazyNamed(
  () => import("../pages/dashboard/google-calendar"),
  "GoogleCalendarLayout"
);
const GoogleCalendarDiagnosePage = lazyNamed(
  () => import("../pages/dashboard/google-calendar/diagnose"),
  "GoogleCalendarDiagnosePage"
);
const GoogleCalendarGuidePage = lazyNamed(
  () => import("../pages/dashboard/google-calendar/guide"),
  "GoogleCalendarGuidePage"
);
const GoogleCalendarAgendaPage = lazyNamed(
  () => import("../pages/dashboard/google-calendar/agenda"),
  "GoogleCalendarAgendaPage"
);
const StudiesLayout = lazyNamed(
  () => import("../pages/dashboard/studies"),
  "StudiesLayout"
);
const StudiesDashboardPage = lazyNamed(
  () => import("../pages/dashboard/studies"),
  "StudiesDashboardPage"
);
const StudyDetailPage = lazyNamed(
  () => import("../pages/dashboard/studies"),
  "StudyDetailPage"
);
const StudiesHistoryPage = lazyNamed(
  () => import("../pages/dashboard/studies"),
  "StudiesHistoryPage"
);
const StudiesDiagnosticsPage = lazyNamed(
  () => import("../pages/dashboard/studies"),
  "StudiesDiagnosticsPage"
);
const StudyTypeCatalogPage = lazyNamed(
  () => import("../pages/dashboard/studies"),
  "StudyTypeCatalogPage"
);
const SupplierOverlayPage = lazyNamed(
  () => import("../pages/dashboard/studies"),
  "SupplierOverlayPage"
);
const PendingDashboardPage = lazyNamed(
  () => import("../pages/dashboard/pending"),
  "PendingDashboardPage"
);
const QvetTransfersPage = lazyNamed(
  () => import("../pages/dashboard/qvet/transfers"),
  "QvetTransfersPage"
);
const QvetCollectionsPage = lazyNamed(
  () => import("../pages/dashboard/qvet/collections"),
  "QvetCollectionsPage"
);
const QvetCollectionDetailsPage = lazyNamed(
  () => import("../pages/dashboard/qvet/collection-details"),
  "QvetCollectionDetailsPage"
);
const QvetCashClosingsPage = lazyNamed(
  () => import("../pages/dashboard/qvet/cash-closings"),
  "QvetCashClosingsPage"
);
const QvetCashFlowsPage = lazyNamed(
  () => import("../pages/dashboard/qvet/cash-flows"),
  "QvetCashFlowsPage"
);
const QvetPurchaseInvoicesPage = lazyNamed(
  () => import("../pages/dashboard/qvet/purchase-invoices"),
  "QvetPurchaseInvoicesPage"
);
const QvetPurchaseOrdersPage = lazyNamed(
  () => import("../pages/dashboard/qvet/purchase-orders"),
  "QvetPurchaseOrdersPage"
);
const SuppliersPage = lazyNamed(
  () => import("../pages/dashboard/suppliers/SuppliersPage"),
  "SuppliersPage"
);
const QvetWarehousesPage = lazyNamed(
  () => import("../pages/dashboard/qvet/warehouses"),
  "QvetWarehousesPage"
);
const QvetSectionsPage = lazyNamed(
  () => import("../pages/dashboard/qvet/sections"),
  "QvetSectionsPage"
);
const QvetStaffLinksPage = lazyNamed(
  () => import("../pages/dashboard/qvet/staff-links"),
  "QvetStaffLinksPage"
);
const QvetCatalogsPage = lazyNamed(
  () => import("../pages/dashboard/qvet/catalogs"),
  "QvetCatalogsPage"
);
const QvetPayablesPage = lazyNamed(
  () => import("../pages/dashboard/qvet/payables"),
  "QvetPayablesPage"
);
const QvetDeliveryNotesPage = lazyNamed(
  () => import("../pages/dashboard/qvet/delivery-notes"),
  "QvetDeliveryNotesPage"
);
const SupplierPaymentsPage = lazyNamed(
  () => import("../pages/dashboard/supplier-payments"),
  "SupplierPaymentsPage"
);
const QvetStaffPage = lazyNamed(
  () => import("../pages/dashboard/qvet/staff"),
  "QvetStaffPage"
);
const QvetPetsPage = lazyNamed(
  () => import("../pages/dashboard/qvet/pets"),
  "QvetPetsPage"
);
const QvetVisitDiagnosesPage = lazyNamed(
  () => import("../pages/dashboard/qvet/visit-diagnoses"),
  "QvetVisitDiagnosesPage"
);
const QvetVisitsPage = lazyNamed(
  () => import("../pages/dashboard/qvet/visits"),
  "QvetVisitsPage"
);
const OrderPlanningPage = lazyNamed(
  () => import("../pages/dashboard/qvet/order-planning"),
  "OrderPlanningPage"
);
const SupplierOrdersPage = lazyNamed(
  () => import("../pages/dashboard/inventory/supplier-orders"),
  "SupplierOrdersPage"
);
const OrdersPage = lazyNamed(
  () => import("../pages/dashboard/orders"),
  "OrdersPage"
);
const ScheduledSyncLogsPage = lazyNamed(
  () => import("../pages/dashboard/qvet/scheduled-logs"),
  "ScheduledSyncLogsPage"
);
const JobHistoryPage = lazyNamed(
  () => import("../pages/dashboard/qvet/job-history"),
  "JobHistoryPage"
);

// --- AI ---
const ClinicalSummaryPage = lazyNamed(
  () => import("../pages/dashboard/ai/clinical-summary"),
  "ClinicalSummaryPage"
);
const MedicationSheetsPage = lazyNamed(
  () => import("../pages/dashboard/ai/medication-sheets"),
  "MedicationSheetsPage"
);
const CommunicationHelperPage = lazyNamed(
  () => import("../pages/dashboard/ai/communication-helper"),
  "CommunicationHelperPage"
);

// --- Inventory ---
const StockAnalysisPage = lazy(
  () => import("../pages/dashboard/inventory/stock-analysis/StockAnalysisPage")
);
const InventoryReportPage = lazy(
  () => import("../pages/dashboard/inventory/reports/InventoryReportPage")
);

// --- Shift Checklist ---
const ShiftChecklistDashboardPage = lazyNamed(
  () => import("../pages/dashboard/shift-checklist/ShiftChecklistDashboardPage"),
  "ShiftChecklistDashboardPage"
);

// --- Audit / Analysis ---
const AuditLayout = lazyNamed(
  () => import("../pages/dashboard/audit"),
  "AuditLayout"
);
const AnalysisLayout = lazyNamed(
  () => import("../pages/dashboard/analysis"),
  "AnalysisLayout"
);

// --- Catalog ---
const CatalogLayout = lazyNamed(
  () => import("../pages/dashboard/catalog"),
  "CatalogLayout"
);

// --- Cash Reconciliation ---
const CashReconciliationLayout = lazyNamed(
  () => import("../pages/dashboard/cash-reconciliation"),
  "CashReconciliationLayout"
);

// --- Accounting (Cash Flow + Bank Statement) ---
const AccountingPage = lazyNamed(
  () => import("../pages/dashboard/accounting/AccountingPage"),
  "AccountingPage"
);

// --- Performance ---
const PerformanceLayout = lazyNamed(
  () => import("../pages/dashboard/performance/PerformanceLayout"),
  "PerformanceLayout"
);

export const DashboardRouter = () => {
  perfMark("dashboard-router-render");
  // Pages the menu hides by role are also closed by URL (GH106)
  const { isAdmin, isAdminOrManager } = useAuthHook();
  return (
    <Routes>
      {/* HOME */}
      <Route path="home" element={<HomeDashPage />} />

      {/* Users */}
      <Route path="users" element={<Users />} />
      {isAdminOrManager && <Route path="users/newUser" element={<NewUser />} />}
      <Route path="users/:userId" element={<User />} />

      <Route path="collaborators/*" element={<CollaboratorsLayout />} />
      <Route path="employments/*" element={<EmploymentsLayout />} />

      {/* Cleanups */}
      <Route path="cleanups/*" element={<CleanUpsLayoutPage />} />

      {/* RFC */}
      <Route path="rfc" element={<CreateNewRFC />} />
      <Route path="rfc/:rfcId" element={<EditRFC />} />

      {/* AuthLogs */}
      <Route path="authLogs" element={<AuthLogPage />} />

      {/* Misc */}
      <Route path="misc" element={<Misc />} />
      {/* Documentation */}
      <Route path="documentation" element={<DocumentationHub />}>
        <Route index element={<Navigate to="docs" replace />} />
        {/* Legacy redirect: kept so old bookmarks/links still resolve. */}
        <Route path="guides" element={null} />
        <Route path="docs" element={null} />
        <Route path="memos" element={null} />
        <Route path="memos/:memoNumber" element={null} />
        <Route path="compliance" element={null} />
      </Route>
      <Route
        path="documentation/d/new"
        element={<DocumentEditorPage />}
      />
      <Route
        path="documentation/d/:slug/edit"
        element={<DocumentEditorPage />}
      />
      <Route
        path="documentation/d/:slug"
        element={<DocumentViewerPage />}
      />

      {/* FCM */}
      <Route path="fcm" element={<FcmIndex />} />
      <Route path="fcm/fcmPartners" element={<FcmPartnersIndex />} />
      <Route path="fcm/fcmDogs" element={<FcmDogsIndex />} />
      <Route path="fcm/fcmTransfers" element={<FcmTransfersIndex />} />
      <Route path="fcm/fcmTransfers/createNew" element={<FcmTransfer />} />
      <Route path="fcm/fcmTransfers/:id" element={<FcmTransfer />} />
      <Route path="fcm/fcmPackages" element={<FcmPackagesIndex />} />
      <Route path="fcm/fcmPackages/createNew" element={<FcmPackage />} />
      <Route path="fcm/fcmPackages/:fcmPackageId" element={<FcmPackage />} />

      {/* Calendar (top-level) */}
      <Route path="calendar" element={<CalendarPage />} />

      {/* TimeOffRequests */}
      <Route path="time-off-requests/*" element={<TimeOffRequestsLayout />} />

      {/* Tasks */}
      <Route path="tasks" element={<TasksLayout />}>
        <Route path={""} element={<Kanban />} />
        <Route path={"list"} element={<TaskList />} />
        <Route path={"create"} element={<CreateTask />} />
        <Route path={"my-tasks"} element={<MyTasks />} />
        <Route path={"stats"} element={<TaskStats />} />
        <Route path={"report"} element={<TaskReport />} />
        <Route path={":id"} element={<TaskView />} />
        <Route path="*" element={<Navigate to="" />} />
      </Route>

      {/* Work Logs */}
      <Route path="work-logs" element={<WorkLogsLayout />}>
        <Route path="" element={<Navigate to="last-log" />} />
        <Route path={"last-log"} element={<LastLogView />} />
        <Route path={"recent"} element={<RecentLogs />} />
        <Route path={"my-logs"} element={<MyLogs />} />
        <Route path={"all-logs"} element={<AllLogs />} />
        <Route path="*" element={<Navigate to="last-log" />} />
      </Route>

      {/* Attendance */}
      <Route path="attendance-records/*" element={<AttendanceRecordsLayout />} />

      {/* Billing — Client Invoicing + Global Invoices */}
      <Route path="billing" element={<NewBillingLayout />}>
        <Route path="" element={<StaffInvoiceListPage />} />
        <Route path="invoices/new" element={<InvoiceRequestPage />} />
        <Route path="invoices/:id" element={<StaffInvoiceDetailPage />} />
        <Route path="clients" element={<ClientListPage />} />
        <Route path="guia" element={<StaffGuidePage />} />
        <Route path="clients/new" element={<ClientFormPage />} />
        <Route path="clients/:id/invoices" element={<StaffClientInvoicesPage />} />
        <Route path="clients/:id" element={<ClientFormPage />} />
        <Route path="global" element={<GlobalInvoicesPage />} />
        <Route path="credit-notes" element={<CreditNotesPage />} />
        <Route path="*" element={<Navigate to="" />} />
      </Route>

      {/* Notifications */}
      <Route path="notifications" element={<Notifications />} />

      <Route path="activity-register/*" element={<ActivityRegisterLayout />} />
      <Route
        path="controlled-prescriptions/*"
        element={<ControlledPrescriptionLayout />}
      />

      {/* Work Shifts */}
      <Route path="work-shifts/*" element={<WeekShiftsLayout />} />
      <Route path="jobs/*" element={<JobsLayout />} />
      <Route path="branches/*" element={<BranchesLayout />} />
      <Route path="salary-data/*" element={<SalaryDataLayout />} />
      <Route path="public-holidays/*" element={<PublicHolidaysLayout />} />
      <Route
        path="simplified-branch-cash-reconciliation/*"
        element={<SimplifiedBranchCashReconciliationLayout />}
      />
      <Route path="payrolls/*" element={<PayrollsLayout />} />
      <Route path="commissions/*" element={<CommissionsLayout />} />
      <Route path="performance/*" element={<PerformanceLayout />} />
      {/* Legacy: Mi desempeño is now a tab inside Rendimiento. */}
      <Route path="my-performance" element={<Navigate to="/dashboard/performance/me" replace />} />

      {/* Settings */}
      <Route path="settings/company" element={<CompanySettingsPage />} />

      {/* Shift Checklist */}
      <Route path="shift-checklist" element={<ShiftChecklistDashboardPage />} />
      <Route path="shift-checklist/guide" element={<ShiftChecklistDashboardPage />} />
      <Route path="shift-checklist/activities" element={<ShiftChecklistDashboardPage />} />
      <Route path="shift-checklist/by-day" element={<ShiftChecklistDashboardPage />} />
      <Route path="shift-checklist/statistics" element={<ShiftChecklistDashboardPage />} />

      {/* Inventory */}
      <Route path="inventory" element={<QvetInventoryV2Page />} />
      <Route path="stock-analysis" element={<StockAnalysisPage />} />
      <Route
        path="inventory/stock-analysis"
        element={<Navigate to="/dashboard/stock-analysis" replace />}
      />
      <Route path="inventory/reports" element={<InventoryReportPage />} />
      {/* Orders (unified — replaces inventory/order-planning + inventory/supplier-orders) */}
      <Route path="orders" element={<OrdersPage />} />

      {/* Legacy redirects to the unified Orders page */}
      <Route
        path="inventory/order-planning"
        element={<Navigate to="/dashboard/orders?tab=planning" replace />}
      />
      <Route
        path="inventory/supplier-orders"
        element={<Navigate to="/dashboard/orders?tab=supplier" replace />}
      />

      {/* Legacy redirects */}
      <Route path="qvet/inventory" element={<Navigate to="/dashboard/inventory" replace />} />
      <Route path="qvet/order-planning" element={<Navigate to="/dashboard/orders?tab=planning" replace />} />

      {/* QVET Sync - Nested routes */}
      {/*
        Las cuatro entidades que **son nuestras y llevan el espejo de QVET dentro** (clase C) o
        que **apuntan a QVET por llaves** (clase D). GH527 · SP8. Viven a primer nivel, fuera del
        panel de sincronización: aquí no se vigila un barrido, se decide algo.
      */}
      <Route path="warehouses" element={<QvetWarehousesPage />} />
      <Route path="sections" element={<QvetSectionsPage />} />
      {/* GH547: un solo módulo — directorio, vinculación, promociones y condiciones. */}
      <Route path="suppliers" element={<SuppliersPage />} />
      <Route path="supplier-linking" element={<Navigate to="/dashboard/suppliers?tab=pendientes" replace />} />
      <Route path="staff-links" element={<QvetStaffLinksPage />} />

      {/*
        🆕 Las 20 listas de parámetros de QVET, con alta, cambio y baja. GH527 · SP10.
        Eran el único informe del panel de sincronización sin pantalla donde mirarse.
      */}
      <Route path="qvet-catalogs" element={<QvetCatalogsPage />} />

      <Route path="qvet/sync" element={<QvetSyncLayout />}>
        <Route path="" element={<Navigate to="panel" />} />
        <Route path="panel" element={<QvetJobsPage />} />
        <Route path="clients" element={<QvetClientsPage />} />
        <Route path="sales" element={<QvetSalesPage />} />
        <Route path="purchases" element={<QvetPurchasesPage />} />
        <Route path="movements" element={<QvetMovementsPage />} />
        <Route path="transfers" element={<QvetTransfersPage />} />
        <Route path="collections" element={<QvetCollectionsPage />} />
        <Route path="collection-details" element={<QvetCollectionDetailsPage />} />
        <Route path="cash-closings" element={<QvetCashClosingsPage />} />
        <Route path="cash-flows" element={<QvetCashFlowsPage />} />
        <Route path="purchase-invoices" element={<QvetPurchaseInvoicesPage />} />
        <Route path="purchase-orders" element={<QvetPurchaseOrdersPage />} />
        <Route path="inventory-snapshots" element={<QvetInventorySnapshotsPage />} />
        {/*
          🚨 **Almacenes, secciones, proveedores y enlaces de personal YA NO viven aquí.**
          Se movieron fuera el 2026-09-12 — ver más abajo, a primer nivel.

          `/qvet/sync` es el panel de los **espejos puros**: lo que copiamos de QVET y donde no
          hay nada nuestro. Esas cuatro tienen datos nuestros —`hvpData`— así que no son espejos,
          y colgarlas de aquí era el mismo error de criterio que se acababa de sacar del menú:
          agrupar por **de dónde salen los datos** en vez de por qué se hace con ellos.

          Y tenía un síntoma visible: al vivir dentro de este layout heredaban su barra de chips
          **y** encendían «Sync QVET» en el lateral a la vez que su propia entrada.
          *(Javier, 2026-09-12: «lo que yo veo en esta vista de sinc debe ser sólo lo que no
          tiene mis datos… está como mezclado el menú con los paths».)*

          Los redirectores de abajo quedan porque estas rutas estuvieron publicadas.
        */}
        <Route path="suppliers" element={<Navigate to="/dashboard/suppliers" replace />} />
        <Route path="warehouses" element={<Navigate to="/dashboard/warehouses" replace />} />
        <Route path="sections" element={<Navigate to="/dashboard/sections" replace />} />
        <Route path="staff-links" element={<Navigate to="/dashboard/staff-links" replace />} />
        <Route path="payables" element={<QvetPayablesPage />} />
        <Route path="delivery-notes" element={<QvetDeliveryNotesPage />} />
        <Route path="staff" element={<QvetStaffPage />} />
        <Route path="pets" element={<QvetPetsPage />} />
        <Route path="visits" element={<QvetVisitsPage />} />
        <Route path="visit-diagnoses" element={<QvetVisitDiagnosesPage />} />
        {/* «Recetas SITPV» moved to the controlled prescriptions section on 2026-09-19 — SITPV is not QVET. */}
        <Route path="sitpv-prescriptions" element={<Navigate to="/dashboard/controlled-prescriptions/recetas-sitpv" replace />} />
        <Route path="job-history" element={<JobHistoryPage />} />
        <Route path="scheduled-logs" element={<ScheduledSyncLogsPage />} />
        <Route path="*" element={<Navigate to="panel" />} />
      </Route>

      {/* External Studies — tabbed layout */}
      <Route path="studies" element={<StudiesLayout />}>
        <Route index element={<StudiesDashboardPage />} />
        <Route path="history" element={<StudiesHistoryPage />} />
        <Route path="diagnostics" element={<StudiesDiagnosticsPage />} />
        <Route path="catalog" element={<StudyTypeCatalogPage />} />
        <Route path="suppliers" element={<SupplierOverlayPage />} />
      </Route>
      <Route path="studies/:id" element={<StudyDetailPage />} />

      {/* Global Pending */}
      <Route path="pending" element={<PendingDashboardPage />} />

      {/* Supplier Payments (próximos pagos a proveedores) */}
      {isAdminOrManager && <Route path="supplier-payments" element={<SupplierPaymentsPage />} />}

      {/* Contacts */}
      <Route path="contacts/google" element={<GoogleContactsPage />} />

      {/* Google Calendar */}
      <Route path="google-calendar" element={<GoogleCalendarLayout />}>
        <Route index element={<Navigate to="agenda" replace />} />
        <Route path="guide" element={<GoogleCalendarGuidePage />} />
        <Route path="diagnose" element={<GoogleCalendarDiagnosePage />} />
        <Route path="agenda" element={<GoogleCalendarAgendaPage />} />
      </Route>

      {/* AI */}
      <Route path="ai/clinical-summary" element={<ClinicalSummaryPage />} />
      <Route path="ai/medication-sheets" element={<MedicationSheetsPage />} />
      <Route path="ai/communication-helper" element={<CommunicationHelperPage />} />

      {/* Analysis / Audit */}
      {isAdminOrManager && <Route path="audit/*" element={<AuditLayout />} />}
      {isAdmin && <Route path="analysis/*" element={<AnalysisLayout />} />}

      {/* Cash Reconciliation V2 */}
      <Route path="cash-reconciliation/*" element={<CashReconciliationLayout />} />

      {/* Accounting (Cash Flow + Bank Statement) */}
      <Route path="accounting" element={<AccountingPage />} />

      {/* Catalog */}
      <Route path="catalog/*" element={<CatalogLayout />} />

      <Route path="" element={<Navigate to="home" />} />
    </Routes>
  );
};
